import { AlertIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon, LinkExternalIcon, WorkflowIcon, XIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useCiLog } from "../api/hooks";
import type { Evidence } from "../api/types";
import { estimateRemaining, groupChecks, isRunning, latestRun, matrixLabel, type CheckGroup } from "../lib/supervise";
import { cn } from "../lib/utils";
import { LogLines } from "../pages/Ci";
import { runHref } from "../pages/CiRun";
import { Empty, Query } from "./common";
import { Annotations } from "./Details";
import { duration } from "./format";
import { EvidenceIcon } from "./StateLabel";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader } from "./ui/card";
import { List, ListItem } from "./ui/list";
import { SegmentedControl } from "./ui/toggle-group";
import { Tooltip } from "./ui/tooltip";

type Totals = Array<{ job: string; runs: number; pass: number; fail: number; ms: number }>;

const DOT: Record<Evidence["status"], string> = {
  pass: "bg-success-emphasis",
  fail: "bg-danger-emphasis",
  infra_error: "bg-warning-emphasis",
  queued: "bg-warning animate-pulse",
  waiting: "bg-neutral-emphasis/40",
  skipped: "bg-neutral-muted",
};

const problem = (g: CheckGroup) => g.jobs.some((e) => e.status === "fail" || e.status === "infra_error" || isRunning(e));

/**
 * The latest CI run of a change: matrix instances collapse into one row of dots, failures and
 * running jobs come first, and logs open in one drawer under the list instead of a new page.
 */
export function Checks({ repo, evidence, totals = [], compact }: { repo: string; evidence: Evidence[]; totals?: Totals; compact?: boolean }) {
  const run = latestRun(evidence);
  const groups = groupChecks(run);
  const hasProblems = groups.some(problem);
  const [filter, setFilter] = useState<"problems" | "all">(hasProblems ? "problems" : "all");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [log, setLog] = useState<Evidence | null>(null);
  if (!run.length)
    return (
      <Card>
        <Empty>No CI jobs ran for this change.</Empty>
      </Card>
    );
  const gating = run.filter((e) => e.status !== "skipped");
  const passed = gating.filter((e) => e.status === "pass").length;
  const running = run.some(isRunning);
  const eta = estimateRemaining(run, totals);
  const shown = filter === "problems" && hasProblems ? groups.filter(problem) : groups;
  const last = run[0];
  const toggle = (k: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (!n.delete(k)) n.add(k);
      return n;
    });
  const failed = run.filter((e) => e.status === "fail" || e.status === "infra_error");

  if (compact)
    return (
      <Card className="mt-0">
        <CardHeader>
          <Summary passed={passed} total={gating.length} running={running} eta={eta} failed={failed.length} />
          <span className="ml-auto flex flex-wrap gap-1">
            {groups.map((g) => (
              <Tooltip key={g.name} content={`${g.name}: ${g.jobs.map((j) => `${matrixLabel(j.job)} ${j.status}`).join(", ")}`}>
                <span className="inline-flex gap-px" tabIndex={0} aria-label={`${g.name} ${g.status}`}>
                  {g.jobs.map((j) => (
                    <i key={j.job} className={cn("h-3 w-1.5 rounded-xs", DOT[j.status])} />
                  ))}
                </span>
              </Tooltip>
            ))}
          </span>
        </CardHeader>
      </Card>
    );

  return (
    <Card>
      <CardHeader className="flex-wrap">
        <Summary passed={passed} total={gating.length} running={running} eta={eta} failed={failed.length} />
        <span className="ml-auto flex items-center gap-2">
          {hasProblems && (
            <SegmentedControl
              label="Which checks"
              value={filter}
              onValueChange={(v) => setFilter(v as "problems" | "all")}
              items={[
                { value: "problems", label: "Failing and running" },
                { value: "all", label: "All" },
              ]}
            />
          )}
          <Button asChild size="sm">
            <Link to={runHref(repo, last.change_id, last.commit_sha)}>
              <WorkflowIcon /> Job graph
            </Link>
          </Button>
        </span>
      </CardHeader>
      <List>
        {shown.map((g) => {
          const matrix = g.jobs.length > 1;
          const expanded = open.has(g.name) || (matrix && g.jobs.some((j) => j.status === "fail"));
          return (
            <li key={g.name} className="border-b border-border-muted last:border-b-0">
              <div className="flex items-center gap-2 px-4 py-2">
                {matrix ? (
                  <Button variant="ghost" size="icon-sm" onClick={() => toggle(g.name)} aria-label={expanded ? `Collapse ${g.name}` : `Expand ${g.name}`} aria-expanded={expanded}>
                    {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  </Button>
                ) : (
                  <span className="size-6" />
                )}
                <EvidenceIcon status={g.status} />
                <b className="truncate">{g.name}</b>
                {matrix ? (
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="inline-flex gap-0.5">
                      {g.jobs.map((j) => (
                        <Tooltip key={j.job} content={`${matrixLabel(j.job)}: ${j.status}`}>
                          <i tabIndex={0} aria-label={`${matrixLabel(j.job)} ${j.status}`} className={cn("size-2.5 rounded-xs", DOT[j.status])} />
                        </Tooltip>
                      ))}
                    </span>
                    {g.jobs.filter((j) => j.status === "pass").length}/{g.jobs.length}
                  </span>
                ) : (
                  <JobMeta e={g.jobs[0]} />
                )}
                {!matrix && <LogButton e={g.jobs[0]} active={log} onOpen={setLog} />}
              </div>
              {matrix && expanded && (
                <List className="border-t border-border-muted bg-muted/40">
                  {g.jobs.map((j) => (
                    <ListItem key={j.job} className="gap-2 py-1.5 pl-14">
                      <EvidenceIcon status={j.status} />
                      <span className="truncate">{matrixLabel(j.job)}</span>
                      <JobMeta e={j} />
                      <LogButton e={j} active={log} onOpen={setLog} />
                    </ListItem>
                  ))}
                </List>
              )}
              {g.jobs
                .filter((j) => j.status === "fail" && j.summary)
                .map((j) => (
                  <div key={`s-${j.job}`} className="px-4 pb-3 pl-14">
                    <Annotations summary={j.summary} />
                  </div>
                ))}
            </li>
          );
        })}
      </List>
      {log && <LogDrawer repo={repo} e={log} onClose={() => setLog(null)} />}
    </Card>
  );
}

function Summary({ passed, total, running, eta, failed }: { passed: number; total: number; running: boolean; eta: number | null; failed: number }) {
  return (
    <span className="flex items-center gap-2">
      {failed ? <AlertIcon className="text-danger" /> : running ? <EvidenceIcon status="queued" /> : <CheckIcon className="text-success" />}
      <span>
        {passed} / {total} checks passed
      </span>
      {failed > 0 && <Badge tone="red">{failed} failing</Badge>}
      {running && <span className="text-xs text-muted-foreground">{eta ? `about ${Math.max(1, Math.round(eta / 60_000))} min left` : "running"}</span>}
    </span>
  );
}

function JobMeta({ e }: { e: Evidence }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {e.attempt > 1 && <Badge tone="orange">attempt {e.attempt}</Badge>}
      {e.status === "waiting" ? "waiting for its needs" : duration(e.ms)}
    </span>
  );
}

function LogButton({ e, active, onOpen }: { e: Evidence; active: Evidence | null; onOpen: (e: Evidence | null) => void }) {
  if (!e.log_key && e.status !== "queued") return <span className="ml-auto" />;
  const on = active?.job === e.job && active.attempt === e.attempt;
  return (
    <Button size="sm" variant={on ? "default" : "ghost"} className="ml-auto" aria-pressed={on} onClick={() => onOpen(on ? null : e)}>
      {e.log_key ? "Log" : "Live log"}
    </Button>
  );
}

/** One log at a time, docked under the checks; follows a running job. */
function LogDrawer({ repo, e, onClose }: { repo: string; e: Evidence; onClose: () => void }) {
  const q = useCiLog(repo, e.change_id, e.job, e.attempt);
  const href = `${rp(repo)}/ci/log?change=${e.change_id}&job=${encodeURIComponent(e.job)}&attempt=${e.attempt}`;
  return (
    <CardContent className="border-t p-0">
      <div className="flex items-center gap-2 bg-muted px-4 py-2 text-xs">
        <EvidenceIcon status={e.status} />
        <b className="truncate">{e.job}</b>
        {q.data && !q.data.complete && <Badge tone="orange">live</Badge>}
        <span className="ml-auto flex items-center gap-1">
          <Button asChild size="sm" variant="ghost">
            <Link to={href}>
              <LinkExternalIcon /> Full log
            </Link>
          </Button>
          <Button size="icon-sm" variant="ghost" onClick={onClose} aria-label="Close log">
            <XIcon />
          </Button>
        </span>
      </div>
      <Query q={q}>{(d) => <LogLines text={d.text} complete={d.complete} className="max-h-96" />}</Query>
    </CardContent>
  );
}
